<template>
  <v-dialog
    v-bind="mergedProps"
    v-on="$listeners"
  >
    <template
      #activator="{ on, attrs }"
    >
      <!-- Forward bindings to the default activator slot -->
      <slot
        name="activator"
        :on="on"
        :attrs="attrs"
      />
    </template>

    <template
      #default
    >
      <slot />
    </template>
  </v-dialog>
</template>

<script>
export default {
  name: 'VDialogWrapper',

  props: {
    /**
     * Designate a custom activator when the activator slot is not used. String can be any valid querySelector and Object can be any valid Node.
     */
    activator: {
      type: null,
      default: undefined
    },
    /**
     * Specifies which DOM element that this component should detach to. String can be any valid querySelector and Object can be any valid Node. This will attach to the root v-app component by default.
     */
    attach: {
      type: null,
      default: false
    },
    /**
     * Milliseconds to wait before closing component. Only applies to hover and focus events.
     */
    closeDelay: {
      type: [Number, String],
      default: 0
    },
    /**
     * Applies a custom class to the detached element. This is useful because the content is moved to the beginning of the v-app component (unless the attach prop is provided) and is not targetable by classes passed directly on the component.
     */
    contentClass: {
      type: String,
      default: undefined
    },
    /**
     * Applies the dark theme variant to the component. This will default the components color to white unless you’ve configured your application theme to dark or if you are using the color prop on the component. You can find more information on the Material Design documentation for dark themes.
     */
    dark: {
      type: Boolean,
      default: false
    },
    /**
     * Disables the ability to open the component.
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Will force the components content to render on mounted. This is useful if you have content that will not be rendered in the DOM that you want crawled for SEO.
     */
    eager: {
      type: Boolean,
      default: false
    },
    /**
     * Changes layout for fullscreen display.
     */
    fullscreen: {
      type: Boolean,
      default: false
    },
    /**
     * Hides the display of the overlay.
     */
    hideOverlay: {
      type: Boolean,
      default: false
    },
    /**
     * Detaches the menu content inside of the component as opposed to the document.
     */
    internalActivator: {
      type: Boolean,
      default: false
    },
    /**
     * Applies the light theme variant to the component.
     */
    light: {
      type: Boolean,
      default: false
    },
    /**
     * Sets the maximum width for the component.
     */
    maxWidth: {
      type: [String, Number],
      default: undefined
    },
    /**
     * Disables the bounce effect when clicking outside of a v-dialog's content when using the `persistent` prop.
     */
    noClickAnimation: {
      type: Boolean,
      default: false
    },
    /**
     * Milliseconds to wait before opening component. Only applies to hover and focus events.
     */
    openDelay: {
      type: [String, Number],
      default: 0
    },
    openOnClick: {
      type: Boolean,
      default: true
    },
    openOnFocus: {
      type: Boolean,
      default: false
    },
    /**
     * Sets the transition origin on the element. You can find more information on the MDN documentation for transition origin.
     */
    origin: {
      type: String,
      default: 'center center'
    },
    /**
     * Sets the overlay color.
     */
    overlayColor: {
      type: String,
      default: undefined
    },
    /**
     * Sets the overlay opacity.
     */
    overlayOpacity: {
      type: [Number, String],
      default: undefined
    },
    /**
     * Clicking outside of the element or pressing esc key will not deactivate it.
     */
    persistent: {
      type: Boolean,
      default: false
    },
    /**
     * Tab focus will return to the first child of the dialog by default. Disable this when using external tools that require focus such as TinyMCE or vue-clipboard.
     */
    retainFocus: {
      type: Boolean,
      default: true
    },
    returnValue: {
      type: null,
      default: undefined
    },
    /**
     * When set to true, expects a v-card and a v-card-text component with a designated height. For more information, check out the scrollable example.
     */
    scrollable: {
      type: Boolean,
      default: false
    },
    /**
     * Sets the component transition. Can be one of the built-in transitions or one your own.
     */
    transition: {
      type: [String, Boolean],
      default: false
    },
    /**
     * Controls whether the component is visible or hidden.
     */
    value: {
      type: null,
      default: undefined
    },
    /**
     * Sets the width for the component.
     */
    width: {
      type: [String, Number],
      default: undefined
    }
  },

  computed: {
    mergedProps () {
      return {
        ...this.$props,
        ...this.$attrs
      }
    }
  }
}
</script>
